import { useEffect, useState } from "react";
import { api, can } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Copy, Wallet, Save } from "lucide-react";
import { toast } from "sonner";

export default function Donate() {
  const { user } = useAuth();
  const [data, setData] = useState({ iban: "", account_holder: "", bank_name: "", description: "" });
  const [editing, setEditing] = useState(false);
  const canEdit = can(user.role, "founder");

  const load = () => api.get("/donate").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const copyIban = async () => {
    const text = data.iban || "";
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text); ok = true;
      } else {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        ok = true;
      }
    } catch (_) { ok = false; }
    toast.success(ok ? "IBAN kopyalandı" : "Kopyalanamadı — manuel kopyalayın");
  };

  const save = async () => {
    try { await api.put("/donate", data); toast.success("Güncellendi"); setEditing(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-3xl">
      <header>
        <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">// SUPPORT</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Donate</h1>
        <p className="text-zinc-500 mt-2">Deulsra'ya destek olmak için IBAN bilgileri.</p>
      </header>

      <Card className="bg-zinc-950 border-zinc-900 overflow-hidden">
        <div className="bg-gradient-to-br from-rose-950/40 to-transparent p-6 border-b border-zinc-900 flex items-center gap-4">
          <div className="w-14 h-14 bg-rose-600/20 border border-rose-600/40 flex items-center justify-center">
            <Wallet className="w-6 h-6 text-rose-400" />
          </div>
          <div>
            <div className="text-[10px] font-mono text-zinc-500 tracking-widest">BANKA HESABI</div>
            <div className="font-heading text-2xl font-bold tracking-tight">{data.bank_name || "—"}</div>
          </div>
        </div>

        {!editing ? (
          <div className="p-6 space-y-6">
            <div>
              <div className="text-[10px] font-mono text-zinc-500 tracking-widest mb-2">IBAN</div>
              <div className="flex items-center gap-2">
                <div data-testid="iban-value" className="font-mono text-lg text-white bg-zinc-900/60 p-4 border-l-2 border-rose-500 flex-1 tracking-wider break-all">
                  {data.iban || "Henüz tanımlanmamış"}
                </div>
                {data.iban && (
                  <Button onClick={copyIban} data-testid="copy-iban-button" className="bg-rose-600 hover:bg-rose-700 h-full">
                    <Copy className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono text-zinc-500 tracking-widest mb-2">HESAP SAHİBİ</div>
              <div className="text-lg" data-testid="donate-holder">{data.account_holder || "—"}</div>
            </div>
            {data.description && (
              <div>
                <div className="text-[10px] font-mono text-zinc-500 tracking-widest mb-2">AÇIKLAMA</div>
                <p className="text-zinc-300 whitespace-pre-wrap">{data.description}</p>
              </div>
            )}
            {canEdit && (
              <Button onClick={()=>setEditing(true)} data-testid="edit-donate-button" variant="outline" className="border-zinc-800">
                Bilgileri Düzenle
              </Button>
            )}
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div><Label className="text-xs font-mono">BANKA</Label>
              <Input value={data.bank_name} onChange={e=>setData({...data,bank_name:e.target.value})} data-testid="donate-bank-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
            <div><Label className="text-xs font-mono">IBAN</Label>
              <Input value={data.iban} onChange={e=>setData({...data,iban:e.target.value})} data-testid="donate-iban-input" className="bg-zinc-900 border-zinc-800 mt-1.5 font-mono" /></div>
            <div><Label className="text-xs font-mono">HESAP SAHİBİ</Label>
              <Input value={data.account_holder} onChange={e=>setData({...data,account_holder:e.target.value})} data-testid="donate-holder-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
            <div><Label className="text-xs font-mono">AÇIKLAMA</Label>
              <Textarea rows={4} value={data.description} onChange={e=>setData({...data,description:e.target.value})} data-testid="donate-description-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
            <div className="flex gap-2">
              <Button onClick={save} data-testid="donate-save-button" className="bg-rose-600 hover:bg-rose-700"><Save className="w-4 h-4 mr-2" /> Kaydet</Button>
              <Button onClick={()=>{setEditing(false); load();}} variant="ghost">İptal</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
